import type { Components, Theme } from "@mui/material/styles";

export const TAB_INDICATOR_SPACING = 0.75; // 6px; MUI spacing is based on 8px

export const MuiTab: Components<Theme>["MuiTab"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      height: theme.spacing(5),
      justifyContent: "center",
      fontSize: "1rem",
      letterSpacing: "1px",
      margin: theme.spacing(0, 2, TAB_INDICATOR_SPACING, 0),
      minWidth: theme.spacing(20),
      minHeight: theme.spacing(5),
      // allow angles of skewed progress indicators to be visible outside button bounds
      overflow: "visible",
      padding: theme.spacing(0, 2, TAB_INDICATOR_SPACING),
      textDecoration: "none",
      textTransform: "none",
      // need to increase specificity above textColorInherit and progress "visited"
      "&.Mui-disabled[role=tab]": {
        opacity: 0.4,
        // the base Button disallows all pointer-events, but that prevents the not-allowed cursor
        pointerEvents: "unset",
        "&:hover": {
          cursor: "not-allowed",
        },
      },
      "&:last-child": {
        marginRight: 0,
      },
      "&.Mui-selected": {
        cursor: "default",
        fontWeight: 800,
        color: theme.palette.text.primary,
      },
      "&::after": {
        backgroundColor: theme.palette.grey[500],
        bottom: 0,
        content: '""',
        display: "block",
        height: theme.spacing(TAB_INDICATOR_SPACING),
        left: 0,
        position: "absolute",
        right: 0,
      },

      variants: Object.entries(theme.palette)
        .filter(
          ([_, values]) =>
            typeof values === "object" && "contrastText" in values,
        )
        .map(([color, values]) => ({
          props: {
            color,
          },
          style: {
            "&.Mui-selected": {
              color: values.contrastText,
            },
          },
        })),
    }),
    textColorInherit: ({ theme }) => ({
      // this is used if "primary" or "secondary" is not specified
      color: theme.palette.grey[500],
      opacity: 1.0,

      // for the progress tabs, if the user has already visited the step
      "&.visited": {
        color: theme.palette.grey[700],
        "&::after": {
          backgroundColor: theme.palette.grey[700],
        },
      },
    }),
  },
};
